import { AsyncPipe, NgClass, UpperCasePipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    inject
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { SpanComponent } from "@components/span/span.component";
import _ from "lodash";
import { Observable, Subject, of } from "rxjs";
import { catchError, map, takeUntil } from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse, Handelssortimentsgrupp, Mattslag } from "~app/common/models/constants";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { TilldelaHandelssortimentsGruppDTO } from "~app/features/virkeskop/forkalkylsmallar/models/TilldelaHandelssortimentsGruppDTO";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { Kontrakt } from "~app/features/virkeskop/models/kontrakt-models";
import { VKDirektutbyte, VKDirektutbyteProduktgruppDTO } from "~app/features/virkeskop/models/tradslag/direktutbyte-models";
import { VKSnabbkalkyl, VKSnabbkalkylProduktGruppDTO } from "~app/features/virkeskop/models/tradslag/snabbkalkyl-models";
import { IVirkeskostnadUtbyte, VKProduktGrupp, Virkeskostnad, VirkeskostnadUtbyte } from "~app/features/virkeskop/models/virkeskostnader/virkeskostnader-models";
import { AtgardMediatorService } from "~app/features/virkeskop/services/atgard-mediator.service";
import { ForkalkylmallarService } from "~app/features/virkeskop/services/forkalkylmallar.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { TradslagMediatorService } from "~app/features/virkeskop/services/tradslag-mediator.service";
import { VirkeskopValidationService } from "~app/features/virkeskop/services/validation/virkeskop-validation.service";
import { VirkeskostnaderMediatorService } from "~app/features/virkeskop/services/virkeskostnader-mediator.service";
import { VirkeskostnaderUtbyteService } from "~app/features/virkeskop/services/virkeskostnader-utbyte.service";
import { VirkeskopValidationIconPopoverComponent } from "../../../validation/validation-icon-popover/virkeskop-validation-icon-popover.component";

type ProduktgruppDTO = VKSnabbkalkylProduktGruppDTO | VKDirektutbyteProduktgruppDTO;

@Component({
    selector: "rask-beraknat-utbyte-button",
    templateUrl: "./beraknat-utbyte-button.component.html",
    styleUrls: ["./beraknat-utbyte-button.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SpanComponent,
        NgClass,
        ButtonComponent,
        VirkeskopValidationIconPopoverComponent,
        AsyncPipe,
        UpperCasePipe
    ]
})

export class BeraknatUtbyteButtonComponent implements OnInit, OnDestroy {
    @ViewChild("utbytePanel") saveMallPanel: ElementRef;

    protected direktutbyteModel: VKDirektutbyte;
    protected disabled = false;
    protected loaded = false;
    protected messageId = [
        "beraknatUtbyteSnabbkalkylChanged",
        "beraknatUtbyteDirektutbyteChanged",
        "beraknatUtbyteArealDiffers"
    ];
    protected showUtbytePanel = false;
    protected snabbkalkylModel: VKSnabbkalkyl;
    protected spanColor$: Observable<string>;
    protected virkeskostnadberaknatUtbyte: VirkeskostnadUtbyte;

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly ejHanteratUtbyte: VKProduktGrupp[] = [];
    private readonly forkalkylmallarService = inject(ForkalkylmallarService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly matchadeVirkeskostnader: Virkeskostnad[] = [];
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly raskModalService = inject(RaskModalService);
    private readonly tradslagMediatorService = inject(TradslagMediatorService);
    private readonly virkeskopValidationService = inject(VirkeskopValidationService);
    private readonly virkeskostnaderMediatorService = inject(VirkeskostnaderMediatorService);
    private readonly virkeskostnaderUtbyteService = inject(VirkeskostnaderUtbyteService);
    private readonly zone = inject(NgZone);

    private allaHandelssortimentsGrupper: TilldelaHandelssortimentsGruppDTO[] = [];
    private kontrakt: Kontrakt;
    private utbyteTotalVolymM3fub = 0;
    private virkeskostnader: Virkeskostnad[] = [];
    private vkatgard: Atgard;

    static readonly utbyteMetodDirektutbyte = "Direktutbyte";
    static readonly utbyteMetodSnabbkalkyl = "Snabbkalkyl";

    protected get entityGuid() {
        return this.atgardMediatorService.atgard.guid;
    }

    protected get utbyteInlastFran() {
        return this.vkatgard?.utbyteInlastFran;
    }

    static arealDiffers(nettoareal: number, utbyteInlastAreal?: number): boolean {
        if (nettoareal && utbyteInlastAreal) {
            const difference = _.round(Math.abs(utbyteInlastAreal - nettoareal), 1);
            return difference > 0.1;
        }
        return false;
    }

    clickout = (event: any) => {
        if (!this.showUtbytePanel) {
            return;
        }

        if (!this.saveMallPanel.nativeElement.contains(event.target)) {
            this.closePopup();
            this.cdRef.detectChanges();
        }
    };

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        document.removeEventListener("click", this.clickout);
    }

    ngOnInit(): void {
        this.spanColor$ = this.virkeskopValidationService
            .filterVisibleMessages(this.messageId, undefined, this.entityGuid)
            .pipe(map(messages => messages.length > 0 ? "text-danger" : "text-success"));

        this.zone.runOutsideAngular(() => {
            document.addEventListener("click", this.clickout);
        });

        this.virkeskostnadberaknatUtbyte = new VirkeskostnadUtbyte();
        this.snabbkalkylModel = new VKSnabbkalkyl();
        this.direktutbyteModel = new VKDirektutbyte();

        // Om nya VK -> hämta via mediator
        this.kontrakt = this.kontraktMediatorService.kontrakt;
        this.vkatgard = this.atgardMediatorService?.atgard;

        this.tradslagMediatorService?.loaded$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.getTradslagUtbyte();
                this.handleChangesInTradslag();

                this.tradslagMediatorService.onSnabbkalkylCalculated$
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(snabbkalkyl => {
                        if (!_.isNil(snabbkalkyl)) {
                            this.snabbkalkylModel = snabbkalkyl;
                            this.handleChangesInTradslag();
                        }
                    });
                this.tradslagMediatorService.onDirektutbyteCalculated$
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(direktutbyte => {
                        if (!_.isNil(direktutbyte)) {
                            this.direktutbyteModel = direktutbyte;
                            this.handleChangesInTradslag();
                        }
                    });

                this.loaded = true;
            });

        if (!this.kontrakt.isLeveransvirke) {
            this.virkeskostnaderMediatorService.virkeskostnader$
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(event => {
                    const atgarderVirkeskostnader = event?.virkeskostnader?.atgarderVirkeskostnader;
                    const atgardVirkeskostnader = atgarderVirkeskostnader?.find(x =>
                        x.virkeskopsatgardGUID === this.vkatgard?.guid);
                    this.virkeskostnader = atgardVirkeskostnader?.virkeskostnader;
                    this.virkeskostnaderUtbyteService.getMattslagsKonverteringar(this.virkeskostnader);
                });
        } else {
            // leveransvirke - virkeskostnader ligger på kontrakt ej åtgärd
            this.virkeskostnader = [];
        }

        this.atgardMediatorService?.atgardUpdate$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(atg => {
                this.vkatgard = atg;
                this.handleChangesInTradslag();
            });

        this.forkalkylmallarService.getHandelssortimentGrupper().subscribe(grupper => this.allaHandelssortimentsGrupper = grupper);
    }

    protected closePopup() {
        this.showUtbytePanel = false;
    }

    protected getBeraknatUtbyte(): void {
        if (!_.isNil(this.snabbkalkylModel.utbyte?.produktgrupper) && !_.isNil(this.direktutbyteModel.utbyte?.produktgrupper)) {
            // Om det finns både direktutbyte och snabbkalkyl visa dialog för importval
            this.openPopup();
        } else if (!_.isNil(this.snabbkalkylModel.utbyte?.produktgrupper)) {
            this.getSnabbkalkyl();
        } else if (!_.isNil(this.direktutbyteModel.utbyte?.produktgrupper)) {
            this.getDirektUtbyte();
        } else {
            return;
        }
    }

    protected getDirektUtbyte(): void {
        this.handleVirkeskostnaderExists().subscribe(ok => {
            if (ok) {
                const utbyte = this.direktutbyteModel.utbyte.produktgrupper;
                const metod = BeraknatUtbyteButtonComponent.utbyteMetodDirektutbyte;
                this.mapBeraknatUtbyte(utbyte, metod);
                this.applyUtbyte(this.virkeskostnadberaknatUtbyte);
            }
        });
        this.closePopup();
    }

    protected getSnabbkalkyl(): void {
        this.handleVirkeskostnaderExists().subscribe(ok => {
            if (ok) {
                const utbyte = this.snabbkalkylModel.utbyte.produktgrupper;
                const metod = BeraknatUtbyteButtonComponent.utbyteMetodSnabbkalkyl;
                this.mapBeraknatUtbyte(utbyte, metod);
                this.applyUtbyte(this.virkeskostnadberaknatUtbyte);
            }
        });
        this.closePopup();
    }

    protected getUtbyteInlastTid(): string {
        return DateUtils.formatDateString(this.vkatgard?.utbyteInlastTid, DateUtils.datetimeformatWithoutSeconds);
    }

    protected handleChangesInTradslag(): void {
        this.disabled = ((_.isNil(this.snabbkalkylModel?.utbyte?.produktgrupper)
            || this.snabbkalkylModel?.utbyte?.produktgrupper?.length === 0) &&
            (_.isNil(this.direktutbyteModel?.utbyte?.produktgrupper)
                || this.direktutbyteModel?.utbyte?.produktgrupper?.length === 0));
    }

    protected handleVirkeskostnaderExists(): Observable<boolean> {
        if (this.virkeskostnader?.length === 0) {
            return of(true);
        } else {
            return this.raskModalService.confirm(new ModalSettings("Hämtning av beräknat utbyte",
                "Vill du ersätta fördelning av trädslag och volymer med rader från trädslagsberäkningen?"))
                .pipe(map(x => x === ConfirmDialogResponse.save ? true : false))
                .pipe(catchError(() => of(false)));
        }
    }

    protected hasUtbyteInlastTid(): boolean {
        return !_.isNil(this.vkatgard?.utbyteInlastTid);
    }

    protected openPopup() {
        setTimeout(() => {
            this.showUtbytePanel = true;
        }, 100);
    }

    private applyUtbyte(virkeskostnadUtbyte: IVirkeskostnadUtbyte): void {
        this.matchadeVirkeskostnader.splice(0);
        this.ejHanteratUtbyte.splice(0);

        virkeskostnadUtbyte.utbyte.forEach(grupp => {
            this.mapUtbyteToVirkeskostnad(grupp);
        });

        // Måste vara heltal
        this.virkeskostnader.forEach(vk => {
            vk.kvantitet = _.round(vk.kvantitet);
            vk.utbyteInlastKvantitet = vk.kvantitet;
        });

        this.handleEjMatchadeVirkeskostnader();

        this.utbyteTotalVolymM3fub = this.virkeskostnadberaknatUtbyte.utbyte.reduce((t, grupp) => t + grupp.kvantitetHa, 0) * (this.vkatgard.nettoareal ?? 0);
        // Uppdatera virkeskostnader
        this.virkeskostnader.forEach(rad => {
            this.virkeskostnaderMediatorService.update(this.kontrakt.guid, this.vkatgard.guid, rad, this);
        });
        // --hantera konstnader som ej kan mappas.
        if (this.ejHanteratUtbyte.length > 0) {
            this.virkeskostnaderUtbyteService.handleEjHanteratUtbyte(
                this.vkatgard.nettoareal,
                this.virkeskostnader,
                this.ejHanteratUtbyte,
                this.utbyteTotalVolymM3fub);
        }
    }

    private getTradslagUtbyte(): void {
        _.assign(this.snabbkalkylModel, this.tradslagMediatorService.snabbkalkyl);
        _.assign(this.direktutbyteModel, this.tradslagMediatorService.direktutbyte);
    }

    private handleEjMatchadeVirkeskostnader(): void {
        this.virkeskostnader.forEach(vk => {
            if (!this.matchadeVirkeskostnader.some(x => x.handelssortimentId === vk.handelssortimentId
                && x.handelssortimentsgruppId === vk.handelssortimentsgruppId)) {
                // Sätt kvantitet till 0
                vk.kvantitet = 0;
                vk.utbyteInlastKvantitet = 0;
            }
        });
    }

    private isKlentimmerTallOrGran(produktGrupp: VKProduktGrupp): boolean {
        const hsgKlentimmerTallOrGran = this.allaHandelssortimentsGrupper
            .filter(x => x.kod === Handelssortimentsgrupp.KlentimmerTall || x.kod === Handelssortimentsgrupp.KlentimmerGran)
            .map(h => h.id);

        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (hsgKlentimmerTallOrGran.some(h => h === produktGrupp.produktgrupp.handelssortimentsgruppId)) {
            return true;
        }
        return false;
    }

    private mapBeraknatUtbyte(utbyte: ProduktgruppDTO[], metod: string): void {
        this.virkeskostnadberaknatUtbyte.utbyte = utbyte;
        this.vkatgard.utbyteInlastFran = metod;
        this.vkatgard.utbyteInlastTid = new Date();
        this.vkatgard.utbyteInlastAreal = this.vkatgard.nettoareal;

        this.atgardMediatorService.update(this.vkatgard);
    }

    private mapKostnad(kostnad: Virkeskostnad, kvantitetHa: number) {
        // Multiplicera med nettoareal
        const kvantitetM3Fub = kvantitetHa * (this.vkatgard.nettoareal ?? 0);
        // Om m3fub, uppdatera kvantitet
        if (kostnad.mattslag === Mattslag.M3F) {
            // Om vi ska räkna upp eller sätta värde (då vi redan kan ha satt detta värde en gång i matchningen)
            if (this.matchadeVirkeskostnader.some(x => x.handelssortimentsgruppId === kostnad.handelssortimentsgruppId &&
                x.handelssortimentId === kostnad.handelssortimentId)) {
                kostnad.kvantitet = kostnad.kvantitet + kvantitetM3Fub;
                kostnad.utbyteInlastKvantitet = kostnad.kvantitet;
            } else {
                kostnad.kvantitet = kvantitetM3Fub;
                kostnad.utbyteInlastKvantitet = kostnad.kvantitet;
            }
        } else {
            //  Annars måttslagskonvertering till aktuellt måttslag
            const isHSGrupp = kostnad.handelssortimentsgruppId !== null && kostnad.handelssortimentId === null;
            let konverteringsfaktor: number;

            if (isHSGrupp) {
                konverteringsfaktor = this.virkeskostnaderUtbyteService.getKonverteringsfaktorForHSgrupp(kostnad.handelssortimentsgruppId, kostnad.mattslag);
            } else {
                konverteringsfaktor = this.virkeskostnaderUtbyteService.getKonverteringsfaktorForHS(kostnad.handelssortimentId, kostnad.mattslag);
            }

            kostnad.kvantitet = kvantitetM3Fub / konverteringsfaktor;
            kostnad.utbyteInlastKvantitet = kostnad.kvantitet;
        }

        this.matchadeVirkeskostnader.push(ObjectUtils.deepCopy(kostnad));
    }

    private mapUtbyteToVirkeskostnad(grupp: VKProduktGrupp): void {
        const kostnadHSG = this.virkeskostnader.filter(x =>
            x.handelssortimentsgruppId === grupp.produktgrupp.handelssortimentsgruppId
            && x.handelssortimentId === null)[0];

        const kostnadHS = this.virkeskostnader.filter(x =>
            x.handelssortimentsgruppId === grupp.produktgrupp.handelssortimentsgruppId
            && x.handelssortimentId !== null)[0];

        const klentimmerBarrHSGId = this.allaHandelssortimentsGrupper.find(hsg => hsg.kod === Handelssortimentsgrupp.KlentimmerBarr).id;

        const kostnadHSGBarr = this.virkeskostnader.filter(x =>
            x.handelssortimentsgruppId === klentimmerBarrHSGId
            && x.handelssortimentId === null)[0];

        const kostnadHSBarr = this.virkeskostnader.filter(x =>
            x.handelssortimentsgruppId === klentimmerBarrHSGId
            && x.handelssortimentId !== null)[0];

        // Volymer (m3fub) mappas där det går
        // Mappning i första hand mot HS-grupp
        if (kostnadHSG) {
            this.mapKostnad(kostnadHSG, grupp.kvantitetHa);
        } else if (kostnadHS) {
            // * Om det inte finns en HS-grupp att lägga volymen för viss produktgrupp i letar vi efter specifikt HS som tillhör motsvarande HS-grupp.
            //   All volym läggs i det första HS som hittas.
            this.mapKostnad(kostnadHS, grupp.kvantitetHa);

            // * Om vi inte kan mappa in klentimmer på tall eller gran, ska volymerna mappas till klentimmer barr.
            //   I första hand på gruppnivå
        } else if (kostnadHSGBarr && this.isKlentimmerTallOrGran(grupp)) {
            this.mapKostnad(kostnadHSGBarr, grupp.kvantitetHa);
            //i andra hand på HS-nivå.
        } else if (kostnadHSBarr && this.isKlentimmerTallOrGran(grupp)) {
            this.mapKostnad(kostnadHSBarr, grupp.kvantitetHa);
        } else {
            // "Nya" rader utan HS-grupp/HS ska notifieras användaren -> manuell hantering
            this.ejHanteratUtbyte.push(grupp);
        }
    }
}
